import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from 'react';
import {
  useColorScheme,
  type ColorSchemeName,
} from 'react-native';

import {
  useThemePreferenceStore,
  type ThemePreference,
} from '@/theme/theme-preference-store';

export type { ThemePreference };

export type ResolvedTheme =
  | 'light'
  | 'dark';

export type AppColors = {
  background: string;
  backgroundSecondary: string;

  surface: string;
  surfaceMuted: string;
  surfaceSoft: string;
  surfacePressed: string;

  text: string;
  textStrong: string;
  textBody: string;
  textSecondary: string;
  textMuted: string;
  textInverse: string;

  border: string;
  borderSoft: string;
  divider: string;

  primary: string;
  primaryStrong: string;
  /**
   * Text drawn IN the brand colour, on a NEUTRAL background — a link, an
   * emphasised label. Never a foreground on `primary`: both values are blues
   * chosen to read against `background`, so on a `primary` fill they land at
   * roughly 1.6:1 and disappear. Use `onPrimary` there.
   */
  primaryText: string;
  /**
   * Foreground for anything sitting ON `primary` — button labels, icons,
   * sent-bubble text. The counterpart of `onTint` in `hooks/use-theme`, and it
   * carries the same values so a bubble in Practice matches one in Chat.
   */
  onPrimary: string;
  primarySoft: string;
  primarySurface: string;
  primaryBorder: string;

  success: string;
  successSoft: string;

  warning: string;
  warningSoft: string;

  danger: string;
  dangerStrong: string;
  dangerSoft: string;
  dangerSurface: string;
  dangerBorder: string;

  icon: string;
  iconMuted: string;

  tabBar: string;
  tabBarBorder: string;
  tabBarActive: string;
  tabBarInactive: string;

  overlay: string;
  shadow: string;

  white: string;
  black: string;
};

type AppThemeContextValue = {
  preference: ThemePreference;
  resolvedTheme: ResolvedTheme;
  isDark: boolean;
  colors: AppColors;
  statusBarStyle: 'light' | 'dark';
  setPreference: (
    preference: ThemePreference,
  ) => void;
};

const lightColors: AppColors = {
  background: '#FCFDFF',
  backgroundSecondary: '#F5F8FC',

  surface: '#FFFFFF',
  surfaceMuted: '#FAFBFD',
  surfaceSoft: '#F4F8FF',
  surfacePressed: '#EEF4FD',

  text: '#102D51',
  textStrong: '#153252',
  textBody: '#425873',
  textSecondary: '#7B879A',
  textMuted: '#929DAC',
  textInverse: '#FFFFFF',

  border: '#E1E8F1',
  borderSoft: '#E4EAF2',
  divider: '#E3E9F1',

  primary: '#2468D8',
  primaryStrong: '#2463CB',
  primaryText: '#3568B7',
  onPrimary: '#FFFFFF',
  primarySoft: '#F0F5FD',
  primarySurface: '#F3F7FF',
  primaryBorder: '#DDE7F7',

  success: '#35A86C',
  successSoft: '#EAF8F0',

  warning: '#B7791F',
  warningSoft: '#FFF8E8',

  danger: '#A64A54',
  dangerStrong: '#B4232F',
  dangerSoft: '#FFF3F4',
  dangerSurface: '#FFF0F1',
  dangerBorder: '#F2CDD1',

  icon: '#526C8D',
  iconMuted: '#8491A4',

  tabBar: '#FFFFFF',
  tabBarBorder: '#E1E8F1',
  tabBarActive: '#2468D8',
  tabBarInactive: '#8794A7',

  overlay: 'rgba(12, 29, 50, 0.44)',
  shadow: '#7187A6',

  white: '#FFFFFF',
  black: '#000000',
};

const darkColors: AppColors = {
  background: '#0D1724',
  backgroundSecondary: '#101C2C',

  surface:      '#152235',
  surfaceMuted: '#19283B',
  surfaceSoft: '#1B2C43',
  surfacePressed: '#213650',

  text: '#EFF5FF',
  textStrong: '#DFEAFA',
  textBody: '#C0CDDD',
  textSecondary: '#A8B6C9',
  textMuted: '#8999AD',
  textInverse: '#0D1724',

  border: '#2C3C51',
  borderSoft: '#29394D',
  divider: '#2A3A4E',

  primary: '#6EA8FF',
  primaryStrong: '#91BCFF',
  primaryText: '#B9D3FF',
  onPrimary: '#04111F',
  primarySoft: '#203754',
  primarySurface: '#1D314B',
  primaryBorder: '#34577F',

  success: '#57C98D',
  successSoft: '#17372A',

  warning: '#F1BF67',
  warningSoft: '#3D321D',

  danger: '#FF929B',
  dangerStrong: '#FF7D88',
  dangerSoft: '#3A222B',
  dangerSurface: '#42232D',
  dangerBorder: '#693943',

  icon: '#A9B9CD',
  iconMuted: '#7F8FA3',

  tabBar: '#111E2E',
  tabBarBorder: '#2A3A4E',
  tabBarActive: '#78AEFF',
  tabBarInactive: '#8191A6',

  overlay: 'rgba(1, 7, 14, 0.72)',
  shadow: '#000000',

  white: '#FFFFFF',
  black: '#000000',
};

// The preference store moved to `theme-preference-store.ts`. It is shared
// with `hooks/use-theme`, which resolves the theme for every screen pushed on
// top of a tab — see that file for why keeping it private here was a bug.
const AppThemeContext =
  createContext<AppThemeContextValue | null>(
    null,
  );

function resolveTheme(
  preference: ThemePreference,
  systemTheme: ColorSchemeName,
): ResolvedTheme {
  if (preference === 'light') {
    return 'light';
  }

  if (preference === 'dark') {
    return 'dark';
  }

  return systemTheme === 'dark'
    ? 'dark'
    : 'light';
}

export function AppThemeProvider({
  children,
}: {
  children: ReactNode;
}) {
  const systemTheme = useColorScheme();

  const preference = useThemePreferenceStore(
    (state) => state.preference,
  );
  const setPreference = useThemePreferenceStore(
    (state) => state.setPreference,
  );

  // Native's saved choice cannot be read synchronously, so it arrives here.
  // A no-op on web, where the store was already seeded with it.
  useEffect(() => {
    void useThemePreferenceStore
      .getState()
      .hydrate();
  }, []);

  const resolvedTheme = resolveTheme(
    preference,
    systemTheme,
  );

  const isDark =
    resolvedTheme === 'dark';

  const value = useMemo<AppThemeContextValue>(
    () => ({
      preference,
      resolvedTheme,
      isDark,
      colors: isDark
        ? darkColors
        : lightColors,
      statusBarStyle: isDark
        ? 'light'
        : 'dark',
      setPreference,
    }),
    [
      preference,
      resolvedTheme,
      isDark,
      setPreference,
    ],
  );

  return (
    <AppThemeContext.Provider value={value}>
      {children}
    </AppThemeContext.Provider>
  );
}

/**
 * The resolved palette, usable ABOVE `AppThemeProvider`.
 *
 * Exists for the root view, which has to be painted before — and outside — the
 * provider. Android's window background is a single static colour
 * (`expo.android.backgroundColor` → `activityBackground` → `windowBackground`)
 * with no dark variant, so anything that uncovers it shows near-white: the
 * keyboard sliding up was leaking a white band into the dark theme. Painting
 * the root view opaquely is what keeps the window from ever being seen.
 *
 * Same inputs and the same `resolveTheme` as the provider, so the two cannot
 * disagree. Prefer `useAppTheme()` anywhere inside the provider.
 */
export function useResolvedAppColors(): AppColors {
  const systemTheme = useColorScheme();
  const preference = useThemePreferenceStore(
    (state) => state.preference,
  );

  return getAppColors(
    resolveTheme(preference, systemTheme),
  );
}

export function useAppTheme() {
  const context = useContext(
    AppThemeContext,
  );

  if (!context) {
    throw new Error(
      'useAppTheme must be used inside AppThemeProvider.',
    );
  }

  return context;
}

export function getAppColors(
  theme: ResolvedTheme,
): AppColors {
  return theme === 'dark'
    ? darkColors
    : lightColors;
}